import React, { useState, useEffect, useMemo } from 'react';
import { db } from './firebase';
import { collection, onSnapshot, query } from 'firebase/firestore';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { FaProjectDiagram, FaExclamationTriangle, FaCheckCircle, FaShoppingCart, FaFilePdf, FaBoxes } from 'react-icons/fa';
import toast, { Toaster } from 'react-hot-toast';
import { pdf } from '@react-pdf/renderer';
import ReporteGeneralPDF from './components/ReporteGeneralPDF';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

function KpiCard({ title, value, icon, colorClass }) {
    // ... (componente sin cambios)
    return (
        <div className="bg-white p-6 rounded-lg shadow-md flex items-center">
            <div className={`mr-4 p-3 rounded-full ${colorClass}`}>{icon}</div>
            <div>
                <p className="text-sm text-gray-500">{title}</p>
                <p className="text-2xl font-bold text-gray-800">{value}</p>
            </div>
        </div>
    );
}

function ReportesView() {
    // ... (estados sin cambios)
    const [proyectos, setProyectos] = useState([]);
    const [inventario, setInventario] = useState([]);
    const [compras, setCompras] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [fechaInicio, setFechaInicio] = useState(new Date(new Date().getFullYear(), 0, 1));
    const [fechaFin, setFechaFin] = useState(new Date());

    useEffect(() => {
        // ... (carga de datos sin cambios)
        const unsubProyectos = onSnapshot(query(collection(db, 'proyectosEnTransito')), snap => setProyectos(snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))));
        const unsubCompras = onSnapshot(query(collection(db, 'compras')), snap => setCompras(snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))));
        let materiaPrimaData = [];
        let accesoriosData = [];
        const updateInventario = () => setInventario([...materiaPrimaData, ...accesoriosData]);
        const unsubMateriaPrima = onSnapshot(query(collection(db, 'materiaPrima')), snap => {
            materiaPrimaData = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            updateInventario();
        });
        const unsubAccesorios = onSnapshot(query(collection(db, 'accesorios')), snap => {
            accesoriosData = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            updateInventario();
        });
        const timer = setTimeout(() => setLoading(false), 2000);
        return () => { 
            unsubProyectos(); unsubCompras(); unsubMateriaPrima(); unsubAccesorios(); clearTimeout(timer);
        };
    }, []);

    const datosFiltrados = useMemo(() => {
        // ... (lógica de filtrado sin cambios)
        const finDelDia = new Date(fechaFin);
        finDelDia.setHours(23, 59, 59, 999);
        const proyectosFiltrados = proyectos.filter(p => {
            const fechaProyecto = p.fechaEntrega?.toDate();
            return fechaProyecto && fechaProyecto >= fechaInicio && fechaProyecto <= finDelDia;
        });
        const comprasFiltradas = compras.filter(c => {
            const fechaCompra = c.fecha?.toDate();
            return fechaCompra && fechaCompra >= fechaInicio && fechaCompra <= finDelDia;
        });
        return { proyectosFiltrados, comprasFiltradas };
    }, [proyectos, compras, fechaInicio, fechaFin]);

    // --- CÁLCULO DEL GRÁFICO CON ORDENACIÓN CORREGIDA ---
    const barChartData = useMemo(() => {
        const data = datosFiltrados.proyectosFiltrados.reduce((acc, proyecto) => {
            const fechaProyecto = proyecto.fechaEntrega?.toDate();
            if (fechaProyecto) {
                const mesKey = fechaProyecto.toLocaleString('es-ES', { month: 'short', year: '2-digit' }).replace('.', '');
                // Creamos una clave numérica para ordenar: AAAA-MM (ej: 202508)
                const sortKey = fechaProyecto.getFullYear() * 100 + fechaProyecto.getMonth();

                let monthData = acc.find(m => m.sortKey === sortKey);
                if (!monthData) {
                    monthData = { name: mesKey, Madera: 0, Softplay: 0, Ambas: 0, General: 0, sortKey };
                    acc.push(monthData);
                }
                monthData[proyecto.departamento || 'General']++;
            }
            return acc;
        }, []);
        
        // Ordenamos el array final basándonos en la clave numérica
        return data.sort((a, b) => a.sortKey - b.sortKey);

    }, [datosFiltrados.proyectosFiltrados]);

    // ... (resto de cálculos y funciones sin cambios)
    const proyectosEnProceso = datosFiltrados.proyectosFiltrados.filter(p => p.estado === 'En Proceso').length;
    const proyectosRetrasados = datosFiltrados.proyectosFiltrados.filter(p => p.estado === 'Retrasado').length;
    const proyectosCompletados = datosFiltrados.proyectosFiltrados.filter(p => p.estado === 'Completado').length;
    const itemsBajoStock = inventario.filter(item => (item.stockMinimo > 0) && (item.cantidad <= item.stockMinimo));
    const valorTotalInventario = inventario.reduce((total, item) => total + ((item.cantidad || 0) * (item.precio || 0)), 0);
    const costoTotalCompras = datosFiltrados.comprasFiltradas.reduce((total, compra) => total + (compra.costoTotal || 0), 0);
    const comprasPorEstado = datosFiltrados.comprasFiltradas.reduce((acc, compra) => {
        const estado = compra.estado || 'Sin Estado';
        acc[estado] = (acc[estado] || 0) + 1;
        return acc;
    }, {});
    const pieDataCompras = Object.keys(comprasPorEstado).map(key => ({ name: key, value: comprasPorEstado[key] }));
    const COLORS_COMPRAS = ['#1ABC9C', '#F39C12', '#3498DB', '#9B59B6'];
    const handleGenerateReportPdf = async () => {
        setPdfLoading(true);
        const toastId = toast.loading('Generando PDF...');
        try {
            const blob = await pdf(<ReporteGeneralPDF proyectos={datosFiltrados.proyectosFiltrados} inventario={inventario} compras={datosFiltrados.comprasFiltradas} />).toBlob();
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `Reporte-HoppyTime-${fechaInicio.toLocaleDateString('es-ES', {day:'2-digit', month:'2-digit', year:'numeric'})}-a-${fechaFin.toLocaleDateString('es-ES', {day:'2-digit', month:'2-digit', year:'numeric'})}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            toast.success("¡PDF generado con éxito!", { id: toastId });
        } catch (error) {
            console.error("Error al generar el PDF:", error);
            toast.error("No se pudo generar el PDF.", { id: toastId });
        }
        setPdfLoading(false);
    };

    if (loading) {
        return <div className="flex justify-center items-center h-full"><p className="text-lg text-gray-500">Cargando Centro de Mando...</p></div>;
    }

    return (
        // ... ( JSX del return sin cambios )
        <div className="h-full flex flex-col p-4 md:p-8 gap-8 bg-gray-50 overflow-y-auto">
            <Toaster position="top-center" />
            <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
                <h1 className="text-3xl font-bold text-gray-800">Centro de Mando</h1>
                <div className="flex items-center gap-4 bg-white p-2 rounded-lg shadow-sm">
                    <div className="flex items-center gap-2">
                        <label className="text-sm font-semibold text-gray-600">Desde:</label>
                        <DatePicker selected={fechaInicio} onChange={date => setFechaInicio(date)} dateFormat="dd/MM/yyyy" className="w-32 border border-gray-300 rounded-md px-2 py-1 shadow-sm" />
                    </div>
                    <div className="flex items-center gap-2">
                         <label className="text-sm font-semibold text-gray-600">Hasta:</label>
                        <DatePicker selected={fechaFin} onChange={date => setFechaFin(date)} dateFormat="dd/MM/yyyy" className="w-32 border border-gray-300 rounded-md px-2 py-1 shadow-sm" />
                    </div>
                </div>
                <button onClick={handleGenerateReportPdf} disabled={pdfLoading} className="bg-red-600 text-white font-bold h-11 px-6 rounded-lg shadow-md hover:bg-red-700 flex items-center gap-2 disabled:bg-gray-400">
                    <FaFilePdf />
                    {pdfLoading ? 'Generando...' : 'Descargar Reporte'}
                </button>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-xl font-bold text-gray-700 mb-4">Visión General de Proyectos (Filtrado)</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                    <KpiCard title="Proyectos en Periodo" value={datosFiltrados.proyectosFiltrados.length} icon={<FaProjectDiagram size={22} className="text-white"/>} colorClass="bg-purple-500" />
                    <KpiCard title="En Proceso / Retrasados" value={`${proyectosEnProceso} / ${proyectosRetrasados}`} icon={<FaExclamationTriangle size={22} className="text-white"/>} colorClass="bg-blue-500" />
                    <KpiCard title="Completados" value={proyectosCompletados} icon={<FaCheckCircle size={22} className="text-white"/>} colorClass="bg-green-500" />
                </div>
                <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={barChartData}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" /><YAxis allowDecimals={false} /><Tooltip /><Legend /><Bar dataKey="Madera" stackId="a" fill="#82ca9d" /><Bar dataKey="Softplay" stackId="a" fill="#8884d8" /><Bar dataKey="Ambas" stackId="a" fill="#ffc658" /><Bar dataKey="General" stackId="a" fill="#a4a4a4" /></BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h2 className="text-xl font-bold text-gray-700 mb-4">Estado del Inventario (Global)</h2>
                    <div className="mb-6"><KpiCard title="Valor Total del Inventario" value={valorTotalInventario.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })} icon={<FaBoxes size={22} className="text-white"/>} colorClass="bg-indigo-500" /></div>
                    <div className="p-4 border rounded-lg">
                        <h3 className="font-semibold text-gray-600 mb-2">⚠️ Alerta de Stock Bajo</h3>
                        {itemsBajoStock.length > 0 ? (
                            <table className="w-full text-left">
                                <thead><tr className="border-b-2"><th className="py-2">Artículo</th><th className="py-2 text-center">Actual</th><th className="py-2 text-center">Mínimo</th></tr></thead>
                                <tbody>{itemsBajoStock.map(item => (<tr key={item.id} className="border-b"><td className="py-2 font-semibold">{item.nombre}</td><td className="py-2 text-center text-red-500 font-bold">{item.cantidad}</td><td className="py-2 text-center">{item.stockMinimo}</td></tr>))}</tbody>
                            </table>
                        ) : ( <p className="text-center py-4 text-gray-500">No hay artículos con stock bajo.</p> )}
                    </div>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h2 className="text-xl font-bold text-gray-700 mb-4">Resumen de Compras (Filtrado)</h2>
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 items-center">
                        <div><KpiCard title="Costo Total en Periodo" value={costoTotalCompras.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })} icon={<FaShoppingCart size={22} className="text-white"/>} colorClass="bg-orange-500" /></div>
                        <div className="h-64">
                             <ResponsiveContainer width="100%" height="100%">
                                <PieChart><Pie data={pieDataCompras} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>{pieDataCompras.map((entry, index) => (<Cell key={`cell-${index}`} fill={COLORS_COMPRAS[index % COLORS_COMPRAS.length]} />))}</Pie><Tooltip /><Legend /></PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ReportesView;